
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import { HistoryItem } from "@/store/modules/assistHistory";
import { logError, logInfo, logWarn } from "@/lib/utils/logger";
import { ASSIST_MESSAGES } from "@/constants/assistMessages";

export default Vue.extend({
  name: "ResultViewContent",
  components: {
    ChatMessage,
    LoadingSpinner,
  },
  props: {
    feedbackText: {
      type: String,
      default: `あなたの作品を読ませていただきました。とても素晴らしい物語でした！

特に印象的だったのは、主人公の心情の変化が丁寧に描かれている点です。読者として、主人公と一緒に成長していく感覚を味わうことができました。

また、世界観の設定も非常に魅力的で、読み進めるうちにその世界に引き込まれていきました。細部まで考え抜かれた設定が、物語の説得力を高めていると感じます。

今後の展開も楽しみにしています。ぜひ続きも読ませてください！`,
    },
  },
  data() {
    return {
      historyData: null as HistoryItem | null,
      isLoadingHistory: false,
      historyError: null as string | null,
    };
  },
  computed: {
    ...mapGetters("assistNavigationModule", ["selectedItemId"]),

    // 履歴表示モードかどうか
    isHistoryMode(): boolean {
      return !!this.selectedItemId;
    },

    // 🆕 履歴のwriterTypeを取得
    historyWriterType(): string | null {
      if (this.isHistoryMode && this.historyData && this.historyData.writerType) {
        return this.historyData.writerType;
      }
      return null;
    },

    // functionTypeに応じた猫の最初のメッセージ
    headerMessage(): string {
      if (this.isHistoryMode && this.historyData && this.historyData.functionType) {
        const type = this.historyData.functionType;
        return ASSIST_MESSAGES[type]?.header || ASSIST_MESSAGES.FEEDBACK_MANUSCRIPT.header;
      }
      return ASSIST_MESSAGES.FEEDBACK_MANUSCRIPT.header;
    },

    // 表示するフィードバックテキスト
    displayFeedbackText(): string {
      if (this.isHistoryMode && this.historyData) {
        if (this.historyData.isError) {
          return "申し訳ございません。この感想の生成中にエラーが発生いたしました。しばらく時間をおいて再度お試しください。";
        }
        if (!this.historyData.completed) {
          return "現在AI読者が感想を執筆中です。しばらくお待ちください...";
        }
        return this.historyData.fullFeedback || "感想データが見つかりませんでした。";
      }
      return this.feedbackText;
    },

    // functionTypeがMATCHING_COMPANYかつfullFeedbackがHTMLの場合true
    isHtmlFeedback(): boolean {
      return (
        this.isHistoryMode &&
        this.historyData &&
        this.historyData.functionType === "MATCHING_COMPANY" &&
        this.historyData.fullFeedback &&
        /<\/?[a-z][\s\S]*>/i.test(this.historyData.fullFeedback)
      );
    },
  },
  async mounted() {
    // 履歴モードの場合は履歴データを取得
    if (this.isHistoryMode) {
      await this.loadHistoryData();
    }
  },
  watch: {
    // selectedItemIdが変更された場合に履歴データを再取得
    selectedItemId: {
      handler(newId: string | null) {
        if (newId) {
          this.loadHistoryData();
        } else {
          this.historyData = null;
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions("assistNavigationModule", ["showHistory"]),
    ...mapActions("assistHistory", ["getHistoryDetail"]),

    async handleBackClick(): Promise<void> {
      try {
        // assist-navigationモジュールのshowHistoryアクションを呼び出して履歴画面に遷移
        await this.showHistory();

        // 従来のback-clickイベントも発火（親コンポーネントでの追加処理用）
        this.$emit("back-click");
      } catch (error) {
        logError({ module: "ResultViewContent", action: "handleBackClick" }, "履歴画面への遷移に失敗", error);
        // エラーが発生した場合でも従来のイベントは発火
        this.$emit("back-click");
      }
    },

    async loadHistoryData(): Promise<void> {
      if (!this.selectedItemId) {
        this.historyData = null;
        return;
      }

      try {
        this.isLoadingHistory = true;
        this.historyError = null;

        logInfo({ module: "ResultViewContent", action: "loadHistoryData" }, "履歴詳細を取得中", {
          selectedItemId: this.selectedItemId,
        });

        // assistHistoryモジュールから履歴詳細を取得
        const detail = await this.getHistoryDetail(this.selectedItemId);

        if (detail) {
          this.historyData = detail;
          logInfo({ module: "ResultViewContent", action: "loadHistoryData" }, "履歴詳細取得完了", {
            id: detail.id,
            completed: detail.completed,
          });
        } else {
          this.historyError = "履歴データが見つかりませんでした";
          logWarn({ module: "ResultViewContent", action: "loadHistoryData" }, "履歴詳細が見つかりません", {
            selectedItemId: this.selectedItemId,
          });
        }
      } catch (error: any) {
        logError({ module: "ResultViewContent", action: "loadHistoryData" }, "履歴詳細の取得に失敗", error);
        this.historyError = error.message || "履歴詳細の取得に失敗しました";
      } finally {
        this.isLoadingHistory = false;
      }
    },
  },
});
